import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VehicleCard from '../components/VehicleCard';
import { Filter, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Marketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [bodyType, setBodyType] = useState(searchParams.get('body_type') || '');
  const [condition, setCondition] = useState(searchParams.get('condition') || '');
  const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'newest');
  // New advanced filters
  const [transmission, setTransmission] = useState(searchParams.get('transmission') || '');
  const [fuelType, setFuelType] = useState(searchParams.get('fuel_type') || '');
  const [color, setColor] = useState(searchParams.get('color') || '');
  const [drivetrain, setDrivetrain] = useState(searchParams.get('drivetrain') || '');
  const [engineSize, setEngineSize] = useState(searchParams.get('engine_size') || '');

  useEffect(() => {
    fetchVehicles();
  }, [searchParams]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (bodyType) params.append('body_type', bodyType);
      if (condition) params.append('condition', condition);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (sortBy) params.append('sort_by', sortBy);
      // New advanced filters
      if (transmission) params.append('transmission', transmission);
      if (fuelType) params.append('fuel_type', fuelType);
      if (color) params.append('color', color);
      if (drivetrain) params.append('drivetrain', drivetrain);
      if (engineSize) params.append('engine_size', engineSize);

      const response = await axios.get(`${API}/vehicles?${params.toString()}`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (bodyType) params.set('body_type', bodyType);
    if (condition) params.set('condition', condition);
    if (minPrice) params.set('min_price', minPrice);
    if (maxPrice) params.set('max_price', maxPrice);
    if (sortBy) params.set('sort_by', sortBy);
    if (transmission) params.set('transmission', transmission);
    if (fuelType) params.set('fuel_type', fuelType);
    if (color) params.set('color', color);
    if (drivetrain) params.set('drivetrain', drivetrain);
    if (engineSize) params.set('engine_size', engineSize);
    
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSearch('');
    setBodyType('');
    setCondition('');
    setMinPrice('');
    setMaxPrice('');
    setSortBy('newest');
    setTransmission('');
    setFuelType('');
    setColor('');
    setDrivetrain('');
    setEngineSize('');
    setSearchParams(new URLSearchParams());
  };

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-heading font-bold mb-2" data-testid="marketplace-title">Vehicle Marketplace</h1>
            <p className="text-textSecondary">{vehicles.length} vehicles available</p>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle-button"
            className="md:hidden bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-4 py-2 flex items-center space-x-2"
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Filters Sidebar - Sticky on desktop with independent scrolling */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-64 flex-shrink-0`}>
            <div className="glass rounded-sm md:sticky md:top-24 md:max-h-[calc(100vh-7rem)] md:overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-bold">Filters</h2>
                  <button onClick={() => setShowFilters(false)} className="md:hidden">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium mb-2">Search</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Make or model"
                    data-testid="filter-search-input"
                    className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>

                {/* Body Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">Body Type</label>
                  <select
                    value={bodyType}
                    onChange={(e) => setBodyType(e.target.value)}
                    data-testid="filter-body-type"
                    className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    <option value="">All Types</option>
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="truck">Truck</option>
                    <option value="luxury">Luxury</option>
                    <option value="electric">Electric</option>
                  </select>
                </div>

                {/* Condition */}
                <div>
                  <label className="block text-sm font-medium mb-2">Condition</label>
                  <select
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    data-testid="filter-condition"
                    className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    <option value="">All Conditions</option>
                    <option value="clean">Clean</option>
                    <option value="salvage">Salvage</option>
                    <option value="damaged">Damaged</option>
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium mb-2">Price Range</label>
                  <div className="space-y-2">
                    <input
                      type="number"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      placeholder="Min price"
                      data-testid="filter-min-price"
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                    <input
                      type="number"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      placeholder="Max price"
                      data-testid="filter-max-price"
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium mb-2">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    data-testid="filter-sort-by"
                    className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    <option value="newest">Newest First</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                  </select>
                </div>

                {/* Advanced Filters */}
                <div className="pt-4 border-t border-white/10">
                  <h3 className="font-heading font-bold mb-4">Advanced Filters</h3>
                  
                  {/* Transmission */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Transmission</label>
                    <select
                      value={transmission}
                      onChange={(e) => setTransmission(e.target.value)}
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="">All</option>
                      <option value="Automatic">Automatic</option>
                      <option value="Manual">Manual</option>
                      <option value="CVT">CVT</option>
                      <option value="Semi-Automatic">Semi-Automatic</option>
                    </select>
                  </div>

                  {/* Fuel Type */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Fuel Type</label>
                    <select
                      value={fuelType}
                      onChange={(e) => setFuelType(e.target.value)}
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="">All</option>
                      <option value="Gasoline">Gasoline</option>
                      <option value="Diesel">Diesel</option>
                      <option value="Electric">Electric</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Plug-in Hybrid">Plug-in Hybrid</option>
                    </select>
                  </div>

                  {/* Color */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Color</label>
                    <select
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="">All</option>
                      <option value="Black">Black</option>
                      <option value="White">White</option>
                      <option value="Silver">Silver</option>
                      <option value="Gray">Gray</option>
                      <option value="Red">Red</option>
                      <option value="Blue">Blue</option>
                      <option value="Green">Green</option>
                      <option value="Brown">Brown</option>
                    </select>
                  </div>

                  {/* Drivetrain */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Drivetrain</label>
                    <select
                      value={drivetrain}
                      onChange={(e) => setDrivetrain(e.target.value)}
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="">All</option>
                      <option value="FWD">FWD</option>
                      <option value="RWD">RWD</option>
                      <option value="AWD">AWD</option>
                      <option value="4WD">4WD</option>
                    </select>
                  </div>

                  {/* Engine Size */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Engine Size</label>
                    <select
                      value={engineSize}
                      onChange={(e) => setEngineSize(e.target.value)}
                      className="w-full bg-surfaceHighlight border border-white/10 text-white rounded-sm px-4 py-2 focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="">All</option>
                      <option value="1.5L">1.5L</option>
                      <option value="2.0L">2.0L</option>
                      <option value="2.5L">2.5L</option>
                      <option value="3.0L">3.0L</option>
                      <option value="3.5L">3.5L</option>
                      <option value="4.0L">4.0L</option>
                      <option value="5.0L">5.0L</option>
                      <option value="6.0L+">6.0L+</option>
                      <option value="Electric">Electric</option>
                    </select>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <button
                    onClick={applyFilters}
                    data-testid="apply-filters-button"
                    className="w-full bg-primary hover:bg-primaryHover text-white rounded-sm font-bold py-2 transition-colors"
                  >
                    Apply Filters
                  </button>
                  <button
                    onClick={clearFilters}
                    data-testid="clear-filters-button"
                    className="w-full bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-sm font-medium py-2 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* Vehicles Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-2xl text-textSecondary">No vehicles found</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 text-primary hover:underline"
                >
                  Clear filters and try again
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.vehicle_id} vehicle={vehicle} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
